import { useEffect, useState } from 'react';

/**
 * Renders a live "MM:SS" countdown driven by `retryAfterSeconds`. Returns the
 * seconds left, or `null` once the countdown has elapsed. Each new
 * `retryAfterSeconds` value resets the countdown.
 */
export function useRateLimitCountdown(retryAfterSeconds: number | null): number | null {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(retryAfterSeconds);

  // Sync the internal counter whenever the caller provides a new lockout duration.
  useEffect(() => {
    setSecondsLeft(retryAfterSeconds);
  }, [retryAfterSeconds]);

  // Tick once per second while a positive countdown is active.
  useEffect(() => {
    if (secondsLeft === null || secondsLeft <= 0) return;
    const handle = window.setTimeout(() => {
      setSecondsLeft((current) => (current !== null && current > 1 ? current - 1 : null));
    }, 1000);
    return () => window.clearTimeout(handle);
  }, [secondsLeft]);

  return secondsLeft;
}

export function formatCountdown(seconds: number): string {
  const mm = Math.floor(seconds / 60).toString().padStart(2, '0');
  const ss = (seconds % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}
